/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useMemo, useContext } from 'react';

import { BULK_USERS_CSV_TEMPLATE_FILENAME } from '../BulkUserCreation.constants';
import { BULK_USER_TRACK_EVENTS } from '../BulkUserCreation.decl';
import { createCsvSampleData, createSampleTableColumns } from '../BulkUserCreation.helpers';
import { BulkUserCreationContext } from '../BulkUserCreationProvider/BulkUserCreationProvider';

import { CompanyCurrency } from './BulkUserCreationPrepareFile.decl';
import { SampleTable, SAMPLE_DATA } from './SampleTable';

import { ArrowSendOutlined, ImportOutlined } from '@aircall/icons';
import { Box, Button, Icon, Spacer, Typography, Flex } from '@aircall/tractor-v2';
import { ExternalLink } from '@components/Link';
import {
  FormWizard,
  useFormWizard,
  CURRENCIES,
  currencyValueWithSymbol,
  DEFAULT_CURRENCY,
} from '@dashboard/library';
import { formatToCsvSyntax } from '@helpers/csv.helpers';
import { getRoleFlags } from '@helpers/roles.helpers';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { usePermissions } from '@hooks/usePermissions';
import { useTracker } from '@hooks/useTracker';
import { saveAs } from 'file-saver';
import { Trans, useTranslation } from 'react-i18next';

export function BulkUserCreationPrepareFile() {
  const { t } = useTranslation();
  const { roles } = usePermissions();
  const { navigateToNextPage } = useFormWizard();
  const { track } = useTracker();
  const { currentPlan, currentCompany } = useGlobalData();
  const context = useContext(BulkUserCreationContext);

  const companyCurrency = (currentCompany.currency ||
    DEFAULT_CURRENCY.toLowerCase()) as CompanyCurrency;
  const monthlyPricePerUser = currentPlan.features.pricePerUser.monthly[companyCurrency];

  const roleFlags = useMemo(() => getRoleFlags(roles), [roles]);
  const tableColumns = useMemo(() => createSampleTableColumns(roleFlags, t), [roleFlags, t]);

  const handleDownload = useCallback(() => {
    const csvData = createCsvSampleData(tableColumns, roleFlags);
    const blob = new Blob([formatToCsvSyntax(csvData)], { type: 'text/csv;charset=utf-8;' });

    saveAs(blob, BULK_USERS_CSV_TEMPLATE_FILENAME);
    track({
      event: BULK_USER_TRACK_EVENTS.TEMPLATE_DOWNLOAD,
      payload: { ...context.state, file_format: 'csv', file_size: blob.size },
    });
  }, [context, roleFlags, tableColumns, track]);

  const handleNext = useCallback(() => {
    navigateToNextPage();
  }, [navigateToNextPage]);

  function handleMoreInfo() {
    track({ event: BULK_USER_TRACK_EVENTS.KNOWLEDGE_BASE_OPENED, payload: context.state });
  }

  return (
    <>
      <FormWizard.PageHeader>
        <FormWizard.PageHeaderTitle>
          {t('bulk_user_creation.prepare_file.title')}
        </FormWizard.PageHeaderTitle>
        <FormWizard.PageHeaderSubtitle>
          <Trans i18nKey='bulk_user_creation.page_header_subtitle'>
            <ExternalLink
              data-test='prepare-file-more-info'
              href={t('bulk_user_creation.prepare_file.title_link')}
              onClick={handleMoreInfo}
            />
          </Trans>
        </FormWizard.PageHeaderSubtitle>
      </FormWizard.PageHeader>

      <FormWizard.PageContent data-test='prepare-file'>
        <Spacer direction='vertical' space='m' fluid>
          <Typography variant='bodyRegularS'>
            <Trans i18nKey='bulk_user_creation.prepare_file.description'>
              <strong />
            </Trans>
          </Typography>
          <Box backgroundColor='neutral-100' borderRadius={32} py='m'>
            <Spacer direction='vertical' space='xxs' alignItems='center' fluid>
              <Typography variant='bodySemiboldS'>
                {t('bulk_user_creation.prepare_file.example_table.title')}
              </Typography>
              <SampleTable columns={tableColumns} data={SAMPLE_DATA} />
              <Typography variant='supportingRegularS'>
                {t('bulk_user_creation.prepare_file.example_table.caption')}
              </Typography>
            </Spacer>
          </Box>
          <Flex justifyContent='center'>
            <Button
              data-test='template-download-link-button'
              mode='outline'
              size='xSmall'
              variant='secondary'
              onClick={handleDownload}
            >
              <ImportOutlined />
              {t('bulk_user_creation.prepare_file.template_donwload_link')}
            </Button>
          </Flex>
          <Spacer space='xs' direction='vertical'>
            <Typography variant='bodySemiboldM'>
              {t('bulk_user_creation.prepare_file.things_to_know.title')}
            </Typography>
            <Spacer space='xxs' direction='vertical'>
              <Spacer space='xxs'>
                <Icon component={ArrowSendOutlined} size={16} color='primary-300' />
                {t('bulk_user_creation.prepare_file.info_banner')}
              </Spacer>

              {monthlyPricePerUser && currentCompany.currency && (
                <Spacer space='xxs' data-test='monthly-price-per-user'>
                  <Icon component={ArrowSendOutlined} size={16} color='primary-300' />
                  {t('bulk_user_creation.prepare_file.things_to_know.text2', {
                    cost: currencyValueWithSymbol(
                      monthlyPricePerUser / 100,
                      currentCompany.currency.toUpperCase() as CURRENCIES
                    ),
                  })}
                </Spacer>
              )}

              <Spacer space='xxs'>
                <Icon component={ArrowSendOutlined} size={16} color='primary-300' />
                {t('bulk_user_creation.prepare_file.things_to_know.text3')}
              </Spacer>
            </Spacer>
          </Spacer>
        </Spacer>
      </FormWizard.PageContent>

      <FormWizard.PageFooter>
        <FormWizard.PageFooterNext onClick={handleNext}>
          {t('bulk_user_creation.prepare_file.next_button')}
        </FormWizard.PageFooterNext>
      </FormWizard.PageFooter>

      <FormWizard.PageSubText>
        <Trans i18nKey='bulk_user_creation.contact_support'>
          <ExternalLink href={t('bulk_user_creation.contact_support_link')} />
        </Trans>
      </FormWizard.PageSubText>
    </>
  );
}
